import { CORE_API_BASELINE } from '../../api/baseline.js';
import { Methods } from '../../api/methods.js';
import { ZaparooClient } from '../../client/client.js';
import { resolvePaths, saveDeviceMetadata } from '../../client/config.js';
import { deviceEndpoint } from '../../client/endpoint.js';
import type { ClientErrorKind } from '../../client/errors.js';
import { resolveDevice } from '../../client/resolver.js';
import { TraceWriter } from '../../client/trace.js';
import { CredentialStore } from '../../crypto/storage.js';
import type { ParsedArgs } from '../args.js';
import { ExitCode } from '../errors.js';
import type { CommandResult } from '../output.js';

type DoctorErrorKind = ClientErrorKind;

const DOCTOR_ERROR_KINDS = new Set<DoctorErrorKind>([
  'connection',
  'timeout',
  'api-auth',
  'encryption-required',
  'pairing-rejected',
  'device-api',
  'unsupported',
  'rate-limit',
  'protocol',
]);

interface DoctorCheck {
  name: string;
  ok: boolean;
  supported?: boolean;
  data?: unknown;
  error?: string;
  kind?: DoctorErrorKind;
}

export async function doctorCommand(args: ParsedArgs): Promise<CommandResult> {
  const checks: DoctorCheck[] = [];
  const remediation: string[] = [];
  const device = await resolveDevice(args.options);
  checks.push({ name: 'device-resolution', ok: true, data: { device: device.id } });

  const paths = resolvePaths(args.options.configPath, args.options.credentialsPath);
  const store = new CredentialStore(paths.credentialsPath);
  const credentials = store.getCredentials(device.id, device.aliases);
  checks.push({
    name: 'stored-credentials',
    ok: true,
    data: {
      present: credentials !== undefined,
      clientId: credentials?.clientId,
      clientName: credentials?.clientName,
    },
  });

  const client = new ZaparooClient(device, {
    credentials,
    connectTimeoutMs: args.options.timeoutSeconds * 1000,
    requestTimeoutMs: args.options.timeoutSeconds * 1000,
    trace: args.options.trace ? new TraceWriter() : undefined,
  });

  let version: { version?: string; platform?: string } | undefined;
  try {
    version = await client.connect();
    try {
      saveDeviceMetadata(device, version, paths.configPath);
    } catch {
      // Diagnostics still report live checks when metadata caching fails.
    }
    checks.push({
      name: 'websocket-version',
      ok: true,
      data: { encrypted: client.encrypted, version },
    });
    try {
      const health = await client.request(Methods.Health);
      checks.push({ name: 'health', ok: true, data: health });
    } catch (error) {
      checks.push(checkFailure('health', error));
    }
    try {
      const current = await client.request(Methods.ClientsCurrent);
      checks.push({ name: 'clients-current', ok: true, supported: true, data: current });
    } catch (error) {
      const failure = checkFailure('clients-current', error);
      checks.push(
        failure.kind === 'unsupported' ? { ...failure, ok: true, supported: false } : failure,
      );
    }
  } catch (error) {
    checks.push(checkFailure('websocket-version', error));
    const kind = errorKind(error);
    if (kind === 'encryption-required') {
      remediation.push(
        'Start pairing on Core, then run: zaparoo-cli pair complete --device <host:port> --pin <pin>',
      );
    } else if (kind === 'pairing-rejected') {
      remediation.push(
        'Saved credentials were rejected. Verify target identity; only run pair forget after user approval, then pair again.',
      );
    } else if (kind === 'api-auth') {
      remediation.push('Configure the matching Core API key for this device.');
    } else if (kind === 'rate-limit') {
      remediation.push('Core rate-limited connection attempts. Wait briefly, then retry.');
    } else {
      remediation.push(
        'Check host, port, Core service state, firewall, and SSH artifact fallback.',
      );
    }
  } finally {
    await client.close();
  }

  if (version?.version) {
    checks.push({
      name: 'api-baseline',
      ok: true,
      data: {
        core: version,
        cliBaseline: CORE_API_BASELINE,
      },
    });
  }

  const ok = checks.every((check) => check.ok);
  const data = {
    ok,
    device: {
      id: device.id,
      endpoint: deviceEndpoint(device).url,
    },
    checks,
    remediation,
  };
  return {
    data,
    human: ok ? `OK ${device.id}` : `Diagnostics failed for ${device.id}`,
    exitCode: ok ? ExitCode.Success : doctorExitCode(checks),
  };
}

function doctorExitCode(checks: DoctorCheck[]): number {
  const kinds = new Set(checks.filter((check) => !check.ok).map((check) => check.kind));
  if (kinds.has('encryption-required') || kinds.has('pairing-rejected')) {
    return ExitCode.EncryptionRequired;
  }
  if (kinds.has('timeout')) return ExitCode.Timeout;
  if (kinds.has('device-api')) return ExitCode.DeviceApi;
  return ExitCode.Connection;
}

function checkFailure(name: string, error: unknown): DoctorCheck {
  return {
    name,
    ok: false,
    error: error instanceof Error ? error.message : String(error),
    kind: errorKind(error),
  };
}

function errorKind(error: unknown): DoctorErrorKind | undefined {
  if (!error || typeof error !== 'object' || !('kind' in error)) return undefined;
  const kind = (error as { kind: unknown }).kind;
  return typeof kind === 'string' && DOCTOR_ERROR_KINDS.has(kind as DoctorErrorKind)
    ? (kind as DoctorErrorKind)
    : undefined;
}
